import {forwardRef} from 'react';
import PublicShortAnswer from './QuestionTypes/PublicShortAnswer';
import PublicMultipleChoice from './QuestionTypes/PublicMultipleChoice';
import PublicCheckbox from './QuestionTypes/PublicCheckbox';
import PublicLinearScale from './QuestionTypes/PublicLinearScale';
import PublicRating from './QuestionTypes/PublicRating';
import PublicDate from './QuestionTypes/PublicDate';
import PublicTime from './QuestionTypes/PublicTime';
import PublicDropdown from './QuestionTypes/PublicDropdown';
import PublicFileUpload from './QuestionTypes/PublicFileUpload';
import PublicRange from './QuestionTypes/PublicRange';
import {FormQuestion} from '../../../services/operations/forms';
import FilePreview from '../../forms/components/AddEditForm/components/QuestionsTab/FilePreview';

type Props = {
  questions: FormQuestion[];
  answers: Record<string, any>;
  errors: Record<string, string>;
  onAnswerChange: (questionId: string, value: any) => void;
};

/**
 * Renders the current page of form questions.
 * Each question card has: title (+ mandatory *), optional image, then
 * the type-specific answer UI.
 */
const PublicFormContent = forwardRef<HTMLDivElement, Props>(
  ({questions, answers, errors, onAnswerChange}, ref) => {
    const renderQuestionBody = (question: FormQuestion) => {
      switch (question?.type) {
        case 'short-answer':
          return (
            <PublicShortAnswer
              value={answers[question?._id] ?? ''}
              onChange={val => onAnswerChange(question?._id, val)}
            />
          );
        case 'multiple-choice':
          return (
            <PublicMultipleChoice
              question={question}
              value={answers[question?._id] ?? null}
              onChange={val => onAnswerChange(question?._id, val)}
            />
          );
        case 'checkbox':
          return (
            <PublicCheckbox
              question={question}
              value={answers[question?._id] ?? null}
              onChange={val => onAnswerChange(question?._id, val)}
            />
          );
        case 'dropdown':
          return (
            <PublicDropdown
              question={question}
              value={answers[question?._id] ?? null}
              onChange={val => onAnswerChange(question?._id, val)}
            />
          );
        case 'linear-scale':
          return (
            <PublicLinearScale
              question={question}
              value={answers[question?._id] ?? null}
              onChange={val => onAnswerChange(question?._id, val)}
            />
          );
        case 'rating':
          return (
            <PublicRating
              question={question}
              value={answers[question?._id] ?? null}
              onChange={val => onAnswerChange(question?._id, val)}
            />
          );
        case 'date':
          return (
            <PublicDate
              question={question}
              value={answers[question?._id] ?? null}
              onChange={val => onAnswerChange(question?._id, val)}
            />
          );
        case 'time':
          return (
            <PublicTime
              question={question}
              value={answers[question?._id] ?? null}
              onChange={val => onAnswerChange(question?._id, val)}
            />
          );
        case 'file-upload':
          return (
            <PublicFileUpload
              question={question}
              value={answers[question?._id]}
              onChange={val => onAnswerChange(question?._id, val)}
            />
          );
        case 'range':
          return (
            <PublicRange
              question={question}
              value={answers[question?._id]}
              onChange={val => onAnswerChange(question?._id, val)}
            />
          );
        default:
          return null;
      }
    };

    return (
      <div ref={ref} className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        <div className="w-full max-w-[1000px] mx-auto flex flex-col gap-2">
          {questions?.map((question, index) => (
            <div
              id={`question-${question?._id}`}
              key={question?._id}
              className="bg-white rounded-lg shadow-md border border-gray90 p-3 md:!p-5">
              {/* Question title */}
              <div className="flex items-start gap-1 mb-1">
                {question?.required && (
                  <span className="text-raspberryRed text-xl font-bold">*</span>
                )}
                <h3 className="m-0 text-xl font-bold text-darkerGray">
                  {question?.title || `Question ${index + 1}`}
                </h3>
              </div>

              {/* Mandatory error */}
              {errors[question?._id] && (
                <p className="m-0 text-[13px] text-raspberryRed mb-2">
                  {errors[question?._id]}
                </p>
              )}

              {/* Question type-specific body */}
              {renderQuestionBody(question)}

              {/* Question file (shared across all types) */}
              {question?.image && (
                <div className="mb-4 flex justify-center">
                  <FilePreview file={question.image} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  },
);

PublicFormContent.displayName = 'PublicFormContent';

export default PublicFormContent;
